/**
 * Serviço de Câmeras
 * Responsável por gerenciar operações com câmeras
 */

import { authService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8090';

export interface Camera {
  id: string;
  organization_id?: string;
  name: string;
  location: string;
  camera_url?: string;
  watch_folder?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  total_detections?: number;
  
  // Fase 43: Campos de captura RTSP
  ip_address: string;
  rtsp_port?: number;
  service_port?: number;
  web_port?: number;
  username?: string;
  password?: string;
  rtsp_url?: string;
  rtsp_path?: string;
  rtsp_transport?: 'tcp' | 'udp' | 'http';
  
  // Configurações de captura
  capture_interval?: number;
  capture_enabled?: boolean;
  image_quality?: number;
  resolution?: string;
  is_entrance?: boolean;  // Se é câmera de entrada (conta no total)
  is_conversion?: boolean;  // 🛒 Câmera de checkout/caixa (taxa de conversão)
  is_exit?: boolean;  // 🚪 Câmera de saída (contagem de pessoas: Entradas - Saídas = pessoas no estabelecimento)
  cooldown_seconds?: number;  // ⏱️ Tempo de cooldown para deduplicação
  confidence_threshold?: number | null;  // 🎯 Threshold de confiança YOLO por câmera (veículos em orgs de veículos)
  person_confidence_threshold?: number | null;  // 👥 Threshold YOLO de PESSOAS por câmera (orgs de veículos)
  inference_size?: number | null;  // 📐 Resolução de inferência YOLO por câmera (320/416/512/640/1280)
  yolo_model?: string | null;  // 🤖 Modelo YOLO por câmera (null = padrão yolov8n)
  night_confidence_threshold?: number | null;  // 🌙 Threshold YOLO para período noturno
  night_hours_start?: number | null;  // 🌙 Hora de início do período noturno (0-23)
  night_hours_end?: number | null;  // 🌙 Hora de fim do período noturno (0-23)

  // 🎭 Análise de Emoções — limiares por câmera (null = padrão global 14px / 0.3)
  // Câmeras de longe/wide-angle: min_face_px menor (10-14); de perto: maior (24+)
  emotion_min_face_px?: number | null;
  emotion_min_confidence?: number | null;
  
  // Status e controle
  status?: 'active' | 'inactive' | 'error' | 'maintenance';
  process_pid?: number;
  last_capture_at?: string;
  last_error?: string;
  error_count?: number;
  
  // Estatísticas
  total_captures?: number;
  successful_captures?: number;
  failed_captures?: number;
  
  enabled?: boolean;
  description?: string;
  brand?: string;
  model?: string;
  
  // 🌐 EdgeAgent Streaming (Ver Ao Vivo sem VPN)
  use_edge_agent?: boolean;

  // � ROI Zones (regiões de interesse para detecção)
  roi_zones?: RoiZone[] | null;

  // 🎯 Linha de contagem direcional (conta só quem entra). null = conta tudo
  counting_line?: CountingLine | null;

  // 📡 Status real da câmera (última atividade)
  last_seen?: string;

  // 🎯 Modo de detecção (person, vehicle, parking, suspect)
  detection_mode?: string;

  // 🎉 Modo Multidão (Crowd Mode) — override manual por câmera
  crowd_mode_override?: boolean | null;  // null=auto, true=ON, false=OFF

  // 🚨 Alerta de Suspeitos — participação por câmera
  suspect_alerts_enabled?: boolean;  // true = participa do alerta de suspeitos

  // 🎉 Horários configurados da organização (vindos do CameraResponse)
  crowd_mode_weekend_start_hour?: number | null;
  crowd_mode_event_start_hour?: number | null;
}

export interface RoiZone {
  id: string;
  name: string;
  points: number[][];  // [[x, y], ...] coordenadas normalizadas 0-1
  enabled: boolean;
  color: string;
}

export interface CountingLine {
  enabled: boolean;
  start: [number, number]; // [x, y] normalizado 0-1
  end: [number, number];   // [x, y] normalizado 0-1
  direction: 1 | -1;       // sentido contado como "entrada"
}

export interface CameraStatus {
  camera_id: string;
  camera_name: string;
  status: string;
  is_capturing: boolean;
  capture_enabled: boolean;
  process_pid: number | null;
  last_capture_at: string | null;
  last_error: string | null;
  error_count: number;
  total_captures: number;
  successful_captures: number;
  failed_captures: number;
  success_rate: number;
}

export interface CameraLog {
  id: string;
  camera_id: string;
  level: 'debug' | 'info' | 'warning' | 'error' | 'critical';
  message: string;
  details?: Record<string, any>;
  created_at: string;
}

export interface CameraStats {
  camera_id: string;
  camera_name: string;
  total_snapshots: number;
  snapshots_with_persons: number;
  last_snapshot_at: string | null;
  errors_24h: number;
  capture_success_rate: number;
}

const STATS_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutos

interface StatsCacheEntry {
  data: CameraStats;
  expiresAt: number;
}

class CameraService {
  private static CACHE_PREFIX = 'awflux_camera_stats_';

  private getStatsFromCache(id: string): CameraStats | null {
    try {
      const raw = localStorage.getItem(CameraService.CACHE_PREFIX + id);
      if (!raw) return null;
      const entry: StatsCacheEntry = JSON.parse(raw);
      if (Date.now() >= entry.expiresAt) {
        localStorage.removeItem(CameraService.CACHE_PREFIX + id);
        return null;
      }
      return entry.data;
    } catch {
      return null;
    }
  }

  private setStatsInCache(id: string, data: CameraStats): void {
    try {
      const entry: StatsCacheEntry = { data, expiresAt: Date.now() + STATS_CACHE_TTL_MS };
      localStorage.setItem(CameraService.CACHE_PREFIX + id, JSON.stringify(entry));
    } catch {
      // localStorage quota exceeded — ignora
    }
  }


  /**
   * Retorna headers padrão para requisições autenticadas
   * Inclui X-Organization-Id para usuários de grupo
   */
  private getHeaders(contentType?: boolean): Record<string, string> {
    const token = authService.getToken();
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${token}`,
    };
    if (contentType) {
      headers['Content-Type'] = 'application/json';
    }
    const activeOrgId = localStorage.getItem('awflux_active_organization_id');
    if (activeOrgId) {
      headers['X-Organization-Id'] = activeOrgId;
    }
    return headers;
  }

  /**
   * Lista todas as câmeras
   */
  async listCameras(): Promise<Camera[]> {
    const response = await fetch(`${API_BASE_URL}/api/v1/cameras`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erro ao carregar câmeras');
    }

    return response.json();
  }

  /**
   * Obtém uma câmera por ID
   */
  async getCamera(cameraId: string): Promise<Camera> {
    const response = await fetch(`${API_BASE_URL}/api/v1/cameras/${cameraId}`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erro ao carregar câmera');
    }

    return response.json();
  }

  /**
   * Cria nova câmera
   */
  async createCamera(data: Partial<Camera>): Promise<Camera> {
    const response = await fetch(`${API_BASE_URL}/api/v1/cameras`, {
      method: 'POST',
      headers: this.getHeaders(true),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao criar câmera' }));
      throw new Error(error.detail || 'Erro ao criar câmera');
    }

    return response.json();
  }

  /**
   * Atualiza câmera existente
   */
  async updateCamera(cameraId: string, data: Partial<Camera>): Promise<Camera> {
    const response = await fetch(`${API_BASE_URL}/api/v1/cameras/${cameraId}`, {
      method: 'PUT',
      headers: this.getHeaders(true),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao atualizar câmera' }));
      throw new Error(error.detail || 'Erro ao atualizar câmera');
    }

    return response.json();
  }

  /**
   * Deleta câmera
   */
  async deleteCamera(cameraId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/v1/cameras/${cameraId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao deletar câmera' }));
      throw new Error(error.detail || 'Erro ao deletar câmera');
    }
  }

  /**
   * Inicia monitoramento de câmera
   */
  async startMonitoring(cameraId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/v1/cameras/${cameraId}/start`, {
      method: 'POST',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao iniciar monitoramento' }));
      throw new Error(error.detail || 'Erro ao iniciar monitoramento');
    }
  }

  /**
   * Para monitoramento de câmera
   */
  async stopMonitoring(cameraId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/v1/cameras/${cameraId}/stop`, {
      method: 'POST',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao parar monitoramento' }));
      throw new Error(error.detail || 'Erro ao parar monitoramento');
    }
  }

  // ========================================
  // Fase 43: Métodos de Captura RTSP
  // ========================================

  /**
   * Testa conexão RTSP da câmera
   */
  async testRTSP(cameraId: string): Promise<{ success: boolean; message: string; details?: any }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/cameras/${cameraId}/test-rtsp`, {
      method: 'POST',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao testar RTSP' }));
      throw new Error(error.detail || 'Erro ao testar RTSP');
    }

    return response.json();
  }

  /**
   * Inicia captura RTSP
   */
  async startCapture(cameraId: string): Promise<{ 
    message: string; 
    camera_id: string; 
    process_pid: number;
    log_path: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/cameras/${cameraId}/start`, {
      method: 'POST',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao iniciar captura' }));
      throw new Error(error.detail || 'Erro ao iniciar captura');
    }

    return response.json();
  }

  /**
   * Para captura RTSP
   */
  async stopCapture(cameraId: string): Promise<{ message: string; camera_id: string }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/cameras/${cameraId}/stop`, {
      method: 'POST',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao parar captura' }));
      throw new Error(error.detail || 'Erro ao parar captura');
    }

    return response.json();
  }

  /**
   * Obtém status da captura
   */
  async getCaptureStatus(cameraId: string): Promise<CameraStatus> {
    const response = await fetch(`${API_BASE_URL}/api/v1/cameras/${cameraId}/capture-status`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao obter status' }));
      throw new Error(error.detail || 'Erro ao obter status');
    }

    return response.json();
  }

  /**
   * Lista logs da câmera
   */
  async getCameraLogs(
    cameraId: string, 
    options?: { level?: string; limit?: number }
  ): Promise<{
    camera_id: string;
    camera_name: string;
    total_logs: number;
    filters: { level: string | null; limit: number };
    logs: CameraLog[];
  }> {
    const params = new URLSearchParams();
    
    if (options?.level) params.append('level', options.level);
    if (options?.limit) params.append('limit', options.limit.toString());
    
    const url = `${API_BASE_URL}/api/v1/cameras/${cameraId}/logs${params.toString() ? `?${params}` : ''}`;
    
    const response = await fetch(url, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao obter logs' }));
      throw new Error(error.detail || 'Erro ao obter logs');
    }

    return response.json();
  }

  /**
   * Obtém estatísticas da câmera (com cache de 5 minutos)
   */
  async getCameraStats(cameraId: string): Promise<CameraStats> {
    const cached = this.getStatsFromCache(cameraId);
    if (cached) return cached;

    const response = await fetch(`${API_BASE_URL}/api/v1/cameras/${cameraId}/stats`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao obter estatísticas' }));
      throw new Error(error.detail || 'Erro ao obter estatísticas');
    }

    const data: CameraStats = await response.json();
    this.setStatsInCache(cameraId, data);
    return data;
  }

  /**
   * Invalida o cache de stats de uma câmera específica
   */
  invalidateStatsCache(cameraId: string): void {
    try { localStorage.removeItem(CameraService.CACHE_PREFIX + cameraId); } catch { /* ignore */ }
  }

  /**
   * Obtém estatísticas de múltiplas câmeras em uma única requisição (batch).
   * IDs já presentes no cache são retornados sem chamada HTTP.
   * Os demais são buscados no endpoint /cameras/stats/batch.
   */
  async getCameraStatsBatch(cameraIds: string[]): Promise<Record<string, CameraStats>> {
    const result: Record<string, CameraStats> = {};
    const idsToFetch: string[] = [];

    // Separar os que já estão em cache
    for (const id of cameraIds) {
      const cached = this.getStatsFromCache(id);
      if (cached) {
        result[id] = cached;
      } else {
        idsToFetch.push(id);
      }
    }

    if (idsToFetch.length === 0) return result;

    const response = await fetch(`${API_BASE_URL}/api/v1/cameras/stats/batch`, {
      method: 'POST',
      headers: this.getHeaders(true),
      body: JSON.stringify({ camera_ids: idsToFetch }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao obter estatísticas em batch' }));
      throw new Error(error.detail || 'Erro ao obter estatísticas em batch');
    }

    const batchResult: Record<string, CameraStats> = await response.json();

    // Popular cache e resultado
    for (const [id, stats] of Object.entries(batchResult)) {
      this.setStatsInCache(id, stats);
      result[id] = stats;
    }

    return result;
  }

  /**
   * Atualiza configurações de captura
   */
  async updateCaptureConfig(
    cameraId: string, 
    config: {
      capture_interval?: number;
      capture_enabled?: boolean;
      image_quality?: number;
      resolution?: string;
    }
  ): Promise<Camera> {
    const response = await fetch(`${API_BASE_URL}/api/v1/cameras/${cameraId}`, {
      method: 'PUT',
      headers: this.getHeaders(true),
      body: JSON.stringify(config),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao atualizar configuração' }));
      throw new Error(error.detail || 'Erro ao atualizar configuração');
    }

    return response.json();
  }

  /**
   * 🚀 Inicia monitoramento da pasta (FolderWatcher)
   * Para processar imagens recebidas via FTP
   */
  async startFolderMonitoring(cameraId: string): Promise<{
    message: string;
    camera_id: string;
    camera_name: string;
    watch_folder: string;
    status: string;
  }> {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/cameras/${cameraId}/start-folder-monitoring`,
      {
        method: 'POST',
        headers: this.getHeaders(),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ 
        detail: 'Erro ao iniciar monitoramento de pasta' 
      }));
      throw new Error(error.detail || 'Erro ao iniciar monitoramento');
    }

    return response.json();
  }

  /**
   * ⏹️ Para monitoramento da pasta (FolderWatcher)
   */
  async stopFolderMonitoring(cameraId: string): Promise<{
    message: string;
    camera_id: string;
  }> {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/cameras/${cameraId}/stop-folder-monitoring`,
      {
        method: 'POST',
        headers: this.getHeaders(),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ 
        detail: 'Erro ao parar monitoramento de pasta' 
      }));
      throw new Error(error.detail || 'Erro ao parar monitoramento');
    }

    return response.json();
  }

  /**
   * 🔍 Verifica status do monitoramento de pasta
   */
  async getFolderMonitoringStatus(cameraId: string): Promise<{
    camera_id: string;
    camera_name: string;
    is_monitoring: boolean;
    watch_folder: string | null;
  }> {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/cameras/${cameraId}/folder-monitoring-status`,
      {
        method: 'GET',
        headers: this.getHeaders(),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ 
        detail: 'Erro ao verificar status do monitoramento' 
      }));
      throw new Error(error.detail || 'Erro ao verificar status');
    }

    return response.json();
  }

  /**
   * 🚀 OTIMIZAÇÃO: Busca status de monitoramento de TODAS as câmeras em uma única requisição
   * 
   * Este método substitui 22 requisições individuais por UMA ÚNICA chamada,
   * evitando sobrecarga no servidor e erros 503.
   * 
   * @returns Dicionário com camera_id como chave e status de monitoramento
   */
  async getBatchFolderMonitoringStatus(): Promise<Record<string, {
    camera_id: string;
    camera_name: string;
    is_monitoring: boolean;
    watch_folder: string | null;
  }>> {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/cameras/batch/folder-monitoring-status`,
      {
        method: 'GET',
        headers: this.getHeaders(),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ 
        detail: 'Erro ao buscar status em lote' 
      }));
      throw new Error(error.detail || 'Erro ao buscar status em lote');
    }

    return response.json();
  }

  /**
   * Navega pelas pastas do sistema
   */
  async browseFolders(path?: string): Promise<{
    current_path: string;
    parent_path: string | null;
    organization_name: string;
    organization_base_path: string;
    is_organization_root: boolean;
    folders: Array<{
      name: string;
      path: string;
      type: string;
      readable: boolean;
      writable: boolean;
    }>;
    files: any[];
    total_folders: number;
    total_files: number;
  }> {
    const url = path 
      ? `${API_BASE_URL}/api/v1/system/browse-folders?path=${encodeURIComponent(path)}`
      : `${API_BASE_URL}/api/v1/system/browse-folders`;
    
    const response = await fetch(url, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao listar pastas' }));
      throw new Error(error.detail || 'Erro ao listar pastas');
    }

    return response.json();
  }

  /**
   * Cria uma nova pasta de câmera dentro da organização
   */
  async createCameraFolder(folderName: string): Promise<{
    message: string;
    folder_name: string;
    folder_path: string;
    organization_name: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/system/create-camera-folder`, {
      method: 'POST',
      headers: this.getHeaders(true),
      body: JSON.stringify({ folder_name: folderName }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao criar pasta' }));
      throw new Error(error.detail || 'Erro ao criar pasta');
    }

    return response.json();
  }

  // ========================================
  // 📐 ROI Zones (Regiões de Interesse)
  // ========================================

  /**
   * Obtém zonas ROI da câmera
   */
  async getCameraROI(cameraId: string): Promise<{ camera_id: string; roi_zones: RoiZone[] | null }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/cameras/${cameraId}/roi`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao obter zonas ROI' }));
      throw new Error(error.detail || 'Erro ao obter zonas ROI');
    }

    return response.json();
  }

  /**
   * Salva zonas ROI da câmera
   */
  async saveCameraROI(cameraId: string, zones: Omit<RoiZone, 'id'>[]): Promise<{ camera_id: string; roi_zones: RoiZone[]; message: string }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/cameras/${cameraId}/roi`, {
      method: 'PUT',
      headers: this.getHeaders(true),
      body: JSON.stringify({ zones }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao salvar zonas ROI' }));
      throw new Error(error.detail || 'Erro ao salvar zonas ROI');
    }

    return response.json();
  }

  /**
   * Remove todas as zonas ROI da câmera
   */
  async deleteCameraROI(cameraId: string): Promise<{ camera_id: string; message: string }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/cameras/${cameraId}/roi`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao remover zonas ROI' }));
      throw new Error(error.detail || 'Erro ao remover zonas ROI');
    }

    return response.json();
  }

  /**
   * Obtém a linha de contagem direcional da câmera (ou null)
   */
  async getCameraCountingLine(cameraId: string): Promise<{ camera_id: string; counting_line: CountingLine | null }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/cameras/${cameraId}/counting-line`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao obter linha de contagem' }));
      throw new Error(error.detail || 'Erro ao obter linha de contagem');
    }

    return response.json();
  }

  /**
   * Salva a linha de contagem direcional da câmera
   */
  async saveCameraCountingLine(cameraId: string, line: CountingLine): Promise<{ camera_id: string; counting_line: CountingLine; message: string }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/cameras/${cameraId}/counting-line`, {
      method: 'PUT',
      headers: this.getHeaders(true),
      body: JSON.stringify({ line }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao salvar linha de contagem' }));
      throw new Error(error.detail || 'Erro ao salvar linha de contagem');
    }

    return response.json();
  }

  /**
   * Remove a linha de contagem (volta a contar todas as pessoas novas)
   */
  async deleteCameraCountingLine(cameraId: string): Promise<{ camera_id: string; message: string }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/cameras/${cameraId}/counting-line`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao remover linha de contagem' }));
      throw new Error(error.detail || 'Erro ao remover linha de contagem');
    }

    return response.json();
  }

  /**
   * Obtém snapshot da câmera (último frame capturado)
   */
  async getCameraSnapshot(cameraId: string): Promise<string> {
    const response = await fetch(`${API_BASE_URL}/api/v1/cameras/${cameraId}/snapshot`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao obter snapshot' }));
      throw new Error(error.detail || 'Erro ao obter snapshot');
    }

    const data = await response.json();
    return data.image_base64;
  }

  /**
   * Obtém frames da última rajada (burst) para animação no preview.
   * Retorna lista de base64 ou null se não houver rajada disponível.
   */
  async getBurstSnapshots(cameraId: string): Promise<string[] | null> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/cameras/${cameraId}/burst-snapshots`,
        { headers: this.getHeaders() },
      );

      if (!response.ok) return null;

      const data = await response.json();
      return data.images_base64 || null;
    } catch {
      return null; // Silencioso: se falhar, cai para snapshot normal
    }
  }

  // ========================================
  // 📡 Live View (EdgeAgent viewer registration)
  // ========================================

  /** Registra como viewer para que o EdgeAgent comece a empurrar frames */
  async startLiveView(cameraId: string): Promise<void> {
    try {
      await fetch(`${API_BASE_URL}/api/v1/edge-agent/start-live/${cameraId}`, {
        method: 'POST',
        headers: this.getHeaders(),
      });
    } catch {
      // Silencioso: se falhar não bloqueia o fluxo
    }
  }

  /** Remove registro de viewer ao fechar o drawer */
  async stopLiveView(cameraId: string): Promise<void> {
    try {
      await fetch(`${API_BASE_URL}/api/v1/edge-agent/stop-live/${cameraId}`, {
        method: 'POST',
        headers: this.getHeaders(),
      });
    } catch {
      // Silencioso
    }
  }
}

export const cameraService = new CameraService();
